import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    Output,
    Renderer2
} from "@angular/core";
import {fromEvent, Subscription, tap} from "rxjs";
import {EditGridReorderEvent} from "../edit-grid-reorder-event";
import {AnalyticsService} from "../../../../../analytics/analytics.service";
import {AnalyticEvent} from "../../../../../analytics/analytic-event";
import {EntityData} from "@fedeltapointofsale/data-client";
import {EditGridService} from "../edit-grid.service";

export class EditGridDragDropData {

    public dropRowIndex: number;
    public dataItemType: string;

    private dataItemJson: string;

    constructor(public editGridUuid?: string,
                dataItem?: EntityData<any>,
                public rowIndex?: number) {
        if (dataItem) {
            this.dataItemJson = dataItem.toJSONString();
            this.dataItemType = dataItem.constructor.name;
        }
    }

    getDataItem(entityDataType: typeof EntityData): EntityData<any> {
        return EntityData.create(entityDataType).assign(JSON.parse(this.dataItemJson));
    }
}

@Component({
    selector: "wbo-edit-grid-reorder-drag-drop",
    templateUrl: "edit-grid-reorder-drag-drop.component.html",
    styleUrls: ["./edit-grid-reorder-drag-drop.component.scss"]
})
export class EditGridReorderDragDropComponent implements AfterViewInit, OnDestroy {

    @Input() editGridUuid: string;
    @Input() dataItem: EntityData<any>;
    @Input() rowIndex: number;
    @Output() reordering: EventEmitter<EditGridReorderEvent> = new EventEmitter<EditGridReorderEvent>();
    @Output() droppedOther: EventEmitter<EditGridDragDropData> = new EventEmitter<EditGridDragDropData>();

    private subscriptions: Subscription = new Subscription();

    constructor(private renderer: Renderer2, private element: ElementRef, private editGridService: EditGridService,
                private analyticsService: AnalyticsService, private ngZone: NgZone) {
    }

    ngAfterViewInit(): void {
        setTimeout(() => this.setupDragAndDrop());
    }

    setupDragAndDrop(): void {
        const item: any = this.findTrElement(this.element.nativeElement);
        if (!item) {
            return;
        }
        this.renderer.setAttribute(item, "draggable", "true");

        const drop = fromEvent(item, "drop");
        this.subscriptions.add(drop.subscribe((dragEvent: DragEvent) => {
            dragEvent.preventDefault();
            dragEvent.stopPropagation();
            const dropRowIndex: number = (<any> this.findTrElement(<Node> dragEvent.target)).rowIndex - 1;

            const editGridDragDropData: EditGridDragDropData = Object.assign(new EditGridDragDropData(), JSON.parse(dragEvent.dataTransfer.getData("text/plain")));
            if (editGridDragDropData.editGridUuid !== this.editGridUuid) {
                editGridDragDropData.dropRowIndex = dropRowIndex;
                this.droppedOther.emit(editGridDragDropData);
                return;
            }

            this.reordering.emit(new EditGridReorderEvent(editGridDragDropData.rowIndex, dropRowIndex));

            const dataItem = this.editGridService.data.splice(editGridDragDropData.rowIndex, 1)[0];

            this.editGridService.data.splice(dropRowIndex, 0, dataItem);
            this.editGridService.refreshGridData();
            this.analyticsService.trackEvent(AnalyticEvent.ENTITY_INTERACTION_GRID_DRAG_AND_DROP);
        }));

        this.ngZone.runOutsideAngular(() => {
            const dragStart = fromEvent(item, "dragstart");
            const dragOver = fromEvent(item, "dragover");

            this.subscriptions.add(dragStart.pipe(
                tap((dragEvent: DragEvent) => {
                    try {
                        dragEvent.dataTransfer.setData("text/plain", JSON.stringify(new EditGridDragDropData(this.editGridUuid, this.dataItem, this.rowIndex)));
                    } catch (err) {
                        console.log(err);
                        // IE doesn't support MIME types in setData
                    }
                })
            ).subscribe((dragEvent: DragEvent) => {
            }));

            this.subscriptions.add(dragOver.subscribe((dragEvent: DragEvent) => {
                dragEvent.preventDefault();
                dragEvent.stopPropagation();
                dragEvent.dataTransfer.dropEffect = "move";
            }));
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    findTrElement(node: Node): Node {
        while (node && !((<Element> node).tagName.toLowerCase() === "tr")) {
            node = node.parentNode;
        }
        return node;
    }
}
